import { zodResolver } from "@hookform/resolvers/zod";
import { KdsModal } from "@kazaar-platform/design-system";
import { useTranslate } from "@tolgee/react";
import { useAtom } from "jotai";
import { isNil } from "lodash";
import { FormProvider, useForm } from "react-hook-form";

import {
  FileContext,
  FileContextProvider,
} from "@/components/FileUploadAsync/FileUploadAsync.context";
import { WorkflowPostponedActivityReadDtoActivityEnum } from "@/config/apiClient";
import { useToasts } from "@/hooks";
import { useOrderItems } from "@/oms/hooks";
import { selectedClientServiceIdAtom } from "@/store";
import {
  type InferredModalUploadDeliveryNoteSchema,
  modalUploadDeliveryNoteFormSchema,
} from "./ModalUploadDeliveryNoteForm.schema";
import { type ModalUploadDeliveryNoteFormProps } from "./ModalUploadDeliveryNoteForm.types";
import { ModalUploadDeliveryNoteFormContent } from "./ModalUploadDeliveryNoteFormContent";

export const ModalUploadDeliveryNoteForm = ({
  orderItem,
  orderItems,
  isOpen,
  setIsOpen,
  onError,
}: ModalUploadDeliveryNoteFormProps) => {
  const { t } = useTranslate();
  const { add } = useToasts();

  const [selectedClientServiceIdAtomValue] = useAtom(
    selectedClientServiceIdAtom
  );

  // Form initialization
  const methods = useForm<InferredModalUploadDeliveryNoteSchema>({
    defaultValues: {
      deliveryNotes: [{}],
    },
    resolver: zodResolver(modalUploadDeliveryNoteFormSchema),
  });

  // Queries
  const { useUpdateOrderItemsDeliveryNote } = useOrderItems();
  const { mutateAsync: updateOrderItemsDeliveryNote, isPending } =
    useUpdateOrderItemsDeliveryNote();

  // Functions
  const handleMarkAsDelivered = async (
    data: InferredModalUploadDeliveryNoteSchema
  ) => {
    const isFinalDeliveryNote = data.validate === "deliveryNote";

    if (!orderItem?.orderId || !orderItem.id) {
      return handleOnError(isFinalDeliveryNote);
    }

    const res = await updateOrderItemsDeliveryNote({
      orderId: orderItem.orderId,
      orderItemId: orderItem.id,
      clientServiceId: selectedClientServiceIdAtomValue,
      orderItemUpdateDeliveryNotesCommand: {
        deliveryNotes: data.deliveryNotes.map((note) => ({
          fileSignedId: note.fileSignedId,
          deliveryDate: note.deliveryDate?.toISOString(),
          orderItemIds: note.orderItemIds,
        })),
        isFinalDeliveryNote: isFinalDeliveryNote,
      },
    });

    if (res.status === 200 || res.status === 201 || res.status === 204) {
      handleOnSuccess(isFinalDeliveryNote);
    } else {
      if (import.meta.env.DEV) {
        console.error("error");
      }
      handleOnError(isFinalDeliveryNote);
    }
  };

  const handleOnSuccess = (isFinalDeliveryNote: boolean) => {
    add({
      state: "success",
      title: t(
        isFinalDeliveryNote
          ? "cardAddDeliveryNote.toast.success"
          : "cardAddDeliveryNote.toast.successPartial"
      ),
    });

    setIsOpen(false);
  };

  const handleOnError = (isFinalDeliveryNote: boolean) => {
    if (onError) {
      onError();
    }

    add({
      state: "error",
      title: t(
        isFinalDeliveryNote
          ? "cardAddDeliveryNote.toast.error"
          : "cardAddDeliveryNote.toast.errorPartial"
      ),
    });

    setIsOpen(false);
  };

  const onValidationError = () => {
    add({
      state: "error",
      title: t("toast.generic.error"),
    });
  };

  if (
    isNil(orderItem) ||
    isNil(orderItem.Workflow) ||
    !orderItem.Workflow.PostponedActivities.some(
      (postponedActivity) =>
        postponedActivity.activity ===
        WorkflowPostponedActivityReadDtoActivityEnum.AddDeliveryNote
    )
  ) {
    return null;
  }

  return (
    <FormProvider {...methods}>
      <FileContextProvider>
        <FileContext.Consumer>
          {(ctx) => (
            <KdsModal
              dialogProps={{
                dialogHeaderProps: {
                  title: t("cardAddDeliveryNote.title"),
                  description: t("cardAddDeliveryNote.description"),
                },
                content: (
                  <ModalUploadDeliveryNoteFormContent
                    methods={methods}
                    orderItems={orderItems}
                  />
                ),
                dialogFooterProps: {
                  buttonProps: [
                    {
                      children: t("buttons.cancel"),
                      color: "neutral",
                      variant: "ghost",
                      isLoading: isPending,
                      disabled: isPending || ctx.isUploading,
                      onPress: () => setIsOpen(false),
                    },
                    {
                      children: t("buttons.confirm"),
                      isLoading: isPending,
                      disabled: isPending || ctx.isUploading,
                      dataCy: "submitDeliveryNoteButton",
                      onPress: () =>
                        void methods.handleSubmit(
                          handleMarkAsDelivered,
                          onValidationError
                        )(),
                    },
                  ],
                  type: "double",
                },
                overflow: true,
              }}
              isOpen={isOpen}
              onOpenChange={() => setIsOpen(!isOpen)}
              size="xl"
            />
          )}
        </FileContext.Consumer>
      </FileContextProvider>
    </FormProvider>
  );
};
